import { camera, scene } from './scene';
import { setCodexGlow, setCodexShadowSpan } from './codex-scene';
/* ============================================================
   圖鑑的取景與比例尺機體
   ============================================================

   從 `codex-model.ts` 拆出來的（B3，行數）。這一支只回答一個問題：
   **鏡頭該退多遠**，讓 0.86m 到 48m 的東西都剛好填滿掃描艙。

   展品的尺寸由 `setFitTarget()` 餵進來，`place()` 只認這三個數字。
   這樣造型層不用知道取景怎麼算，取景層也不用知道展品是 Boss 還是小怪。

   ⚠️ **比例尺參照物不在這裡。** 舊版旁邊站一台半透明的 CHASSIS-0，
   它同時決定了橫向取景的下限（每往外一單位鏡頭就要退一點），
   所以小型敵人永遠填不滿畫面。使用者 2026-08-03 指定拿掉它，
   取景因此可以真的貼著展品走。「多大」改用別的方式表達。 */

/** 展品的尺寸（世界單位＝公尺）。`place()` 只讀這三個。 */
var mh = 2, mw = 2, my = 0;
export function setFitTarget(h, w, y) { mh = h; mw = w; my = y; }


/* ---------- 鏡頭（自動 fit） ---------- */

/** 版面補正：**掃描艙比整張畫布窄／矮多少，鏡頭就退多遠。**
    由 `ui/codex.ts` 量 DOM 之後餵進來 —— 版面與鏡頭各寫一份常數遲早會對不上。

    `k` = 畫布高 ÷ 掃描艙高，`kx` = 畫布寬 ÷ 掃描艙寬，兩個都 ≥ 1。
    ⚠️ **垂直與水平要各補各的。** 舊版只有一個 `k`，而且比例是**反的**
    （寫成掃描艙高 ÷ 畫布高，永遠 < 1 所以被 `Math.max(1, …)` 夾成 1，
    等於整個補正從來沒有作用）。水平則完全沒補 —— 症狀就是比例尺機甲
    有半個身體被右邊的檔案欄蓋住（實看四種尺寸量到的毛病之一）。 */
export var FIT = { k: 1, kx: 1, yaw: 0.6, pitch: 0.16, dist: 10, y: 1.2 };

/** 依目前這一隻的體型算鏡頭距離。**新移植的東西不用調任何參數**。 */
export function place() {
  var h = Math.max(1.6, mh);
  camera.fov = 34;
  camera.updateProjectionMatrix();
  var tv = Math.tan((camera.fov * Math.PI / 180) / 2);
  var asp = camera.aspect || 1.7;

  /* **垂直與水平要各算一次，取比較遠的那個，而且各自套自己的版面補正。**
     fov 是垂直的，而 44m 寬的 SCHLANGE、22m 寬的 KATHEDRALE 難在水平 ——
     只算垂直的話它們會直接溢出畫面兩側（headless 完全看不出來）。 */
  /* ⭐ **畫面上的大小就是比例尺。**

     比例尺機甲拿掉之後（使用者 2026-08-03 指定），如果每一隻都自動填滿畫面，
     2m 的 NADEL 與 48m 的 WELTACHSE 會長得一樣大 —— 等於把「多大」這個
     資訊整個抹掉（使用者實看的回報：「拿掉機甲大小又失常了」）。

     所以取景不是「填滿」而是「填到**該有的比例**」：目標佔比隨體型沿
     對數曲線從 26% 走到 94%。用對數而不是線性，是因為體型差 55 倍 ——
     線性的話 2m 的東西會只剩畫面上的一個點。

       1.4m → 26%    2m → 33%    5m → 46%
       16m → 73%     48m → 94%

     這條曲線是**唯一**的比例線索，改它等於改整本圖鑑的「這東西有多大」。 */
  var H_MIN = 1.4, H_MAX = 48, F_MIN = 0.26, F_MAX = 0.94;
  var sz = Math.max(h, mw * 0.55);
  var u = Math.log(Math.max(H_MIN, Math.min(H_MAX, sz)) / H_MIN) / Math.log(H_MAX / H_MIN);
  var frac = F_MIN + (F_MAX - F_MIN) * u;
  var needV = h / frac;
  var needH = mw / frac;
  var d = Math.max(needV * FIT.k / 2 / tv, needH * FIT.kx / 2 / (tv * asp));
  FIT.dist = d + h * 0.06;
  setCodexGlow(Math.max(mw * 0.5, 0.9));
  setCodexShadowSpan(Math.max(mw * 0.5, 2.5), h);
  /* 注視點要同時照顧到「矮的展品」與「4.46 高的機體」——
     只看展品的話機體的頭會被切掉，而頭是它最好認的部分。 */
  FIT.y = my > 0 ? my : Math.max(1.5, h * 0.48);
  /* **霧要跟著取景走。** 展示台的霧是固定的 16–46（給小怪用的，鏡頭 10 遠），
     而 REQUIEM 44m 高、WELTACHSE 48m 高，鏡頭要退到 90 以外 ——
     整隻落在霧的遠平面之外，畫面上是一片全黑而且 console 零錯誤。
     這是「看起來沒建起來，其實建好了」最難查的一種。 */
  if ((scene.fog as any) && (scene.fog as any).isFog) {
    var fg: any = scene.fog;
    fg.near = Math.max(6, FIT.dist * 0.55);
    fg.far = Math.max(46, FIT.dist * 2.6);
  }
}

/** 每個畫面幀把鏡頭擺到位（`ui/codex.ts` 呼叫）。 */
export function placeCodexCamera(yaw, pitch) {
  var d = FIT.dist, cy = Math.cos(pitch), sy = Math.sin(pitch);
  camera.position.set(Math.sin(yaw) * d * cy, FIT.y + d * sy, Math.cos(yaw) * d * cy);
  camera.lookAt(0, FIT.y, 0);
}
